export class StringUtils {

    // ===============================
    // 1. VALIDATION & NULL CHECKS
    // ===============================

    static isEmpty(val: any): boolean {
        return !val || val === undefined || val === null || val === "" || (val.length !== undefined && val.length === 0);
    }

    static isNotEmpty(str: string | null | undefined): boolean {
        return !this.isEmpty(str);
    }

    static isNotNull(val: any): boolean {
        return !StringUtils.isEmpty(val);
    }

    static isDifferent(val1: string | null | undefined, val2: string | null | undefined): boolean {
        return (val1 ?? "") !== (val2 ?? "");
    }

    static isEmptyAllFields(val: any): boolean {
        for (const key of Object.keys(val)) {
            const value = val[key];
            if (StringUtils.isNotNull(value)) {
                return false;
            }
        }
        return true;
    }

    static isNumber(str: string): boolean {
        if (typeof str !== 'string') {
            return false;
        }

        if (str.trim() === '') {
            return false;
        }

        return !Number.isNaN(Number(str));
    }

    static isJsonString(str: string): boolean {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            return false;
        }
    }

    static contains(str: string, val: string): boolean {
        return str.indexOf(val) !== -1;
    }

    // ===============================
    // 2. BASIC STRING OPERATIONS
    // ===============================

    static replaceAll(str: string, find: string, replace: string): string {
        const escapedFind = find.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return str.replace(new RegExp(escapedFind, 'g'), replace);
    }

    static turkishToEnglish(str: string): string {
        return this.replaceAll(this.replaceAll(
            this.replaceAll(
                this.replaceAll(
                    this.replaceAll(
                        this.replaceAll(
                            this.replaceAll(
                                this.replaceAll(
                                    this.replaceAll(
                                        this.replaceAll(
                                            this.replaceAll(
                                                this.replaceAll(str, 'Ğ', 'G'),
                                                'Ü', 'U'),
                                            'Ş', 'S'),
                                        'İ', 'I'),
                                    'Ö', 'O'),
                                'Ç', 'C'),
                            'ğ', 'g'),
                        'ü', 'u'),
                    'ş', 's'),
                'ı', 'i'),
            'ö', 'o'),
            'ç', 'c');
    }

    static trim(str: string): string {
        return str.replace(/^\s+|\s+$/g, "");
    }

    static ltrim(str: string): string {
        return str.replace(/^\s+/, "");
    }

    static rtrim(str: string): string {
        return str.replace(/\s+$/, "");
    }

    // ===============================
    // 3. CASE OPERATIONS
    // ===============================

    static lower(str: string): string {
        return str.toLowerCase();
    }

    static upper(str: string): string {
        return str.toUpperCase();
    }

    static capFirst(str: string): string | null {
        if (str.length === 1) {
            return str.toUpperCase();
        } else if (str.length > 0) {
            const regex: RegExp = /^(\(|\[|"|')/;
            if (regex.test(str)) {
                return str.substring(0, 2).toUpperCase() + str.substring(2);
            } else {
                return str.substring(0, 1).toUpperCase() + str.substring(1);
            }
        }
        return null;
    }

    static capWords(str: string): string | null {
        const regexp: RegExp = /\s/;
        const words = str.split(regexp);
        if (words.length === 1) {
            return this.capFirst(words[0]);
        } else if (words.length > 1) {
            let result: string = "";
            for (let i = 0; i < words.length; i++) {
                const capWord = this.capFirst(words[i]);
                if (capWord !== null) {
                    result += capWord + " ";
                }
            }
            return this.trim(result);
        }
        return null;
    }

    // ===============================
    // 4. TEXT MANIPULATION
    // ===============================

    static truncateWords(str: string, num: number): string {
        const words: Array<string> = str.split(/\s+/);
        if (words.length > num) {
            return words.slice(0, num).join(" ");
        }
        return words.join(" ");
    }

    static truncateWordsWithHtml(str: string, num: number): string {
        const tags: Array<string> = [];
        let truncation: string = this.truncateWords(str, num);
        const matches: RegExpMatchArray | null = truncation.match(/<[\/]?([^> ]+)[^>]*>/g);

        if (matches) {
            for (let i: number = 0; i < matches.length; i++) {
                const opening: string = matches[i].replace("/", "");
                if (matches[i].indexOf("/") !== -1 && tags.indexOf(opening) !== -1) {
                    const index = tags.indexOf(opening);
                    if (index > -1) tags.splice(index, 1);
                } else if (matches[i].indexOf("/") !== -1) {
                    continue;
                } else {
                    tags.push(matches[i]);
                }
            }
        }

        for (let i: number = 0; i < tags.length; i++) {
            truncation += tags[i]
                .replace("<", "</")
                .replace(/(\s*)(\w+)=("[^<>"]*"|'[^<>']*'|\w+)/g, "");
        }
        return truncation;
    }

    // ===============================
    // 5. HTML OPERATIONS
    // ===============================

    static stripHtml(str: string): string {
        let content: string = str.replace(/<[\/]?([^> ]+)[^>]*>/g, "");
        content = content.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
        content = content.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");
        content = content.replace(/<!--[\s\S]*?-->/g, "");
        content = content.replace("&nbsp;", " ");
        content = content.replace("&amp;", "&");
        return content;
    }

    static escapeHtml(str: string): string {
        let content: string = str.replace(/"/g, "&quot;");
        content = content.replace(/&(?!\w+;)/g, "&amp;");
        content = content.replace(/>/g, "&gt;");
        content = content.replace(/</g, "&lt;");
        return content;
    }

    // ===============================
    // 6. TYPE CONVERSION
    // ===============================

    static toBool(str: string): boolean {
        if (this.isEmpty(str)) {
            return false;
        } else if (
            this.lower(str) === "true" ||
            this.lower(str) === "1" ||
            this.lower(str) === "y" ||
            this.lower(str) === "t"
        ) {
            return true;
        }
        return false;
    }

    // ===============================
    // 7. FORMATTING & URL OPERATIONS
    // ===============================

    static slugify(str: string, lower: boolean = true): string {
        if (lower) {
            return this.lower(str)
                .normalize()
                .replace(/[^a-z0-9]/gi, "-");
        }
        return str.normalize().replace(/[^a-z0-9]/gi, "-");
    }

    static toPhoneNumber(str: string): string {
        try {
            return (
                str.substring(0, 3) +
                "-" +
                str.substring(3, 6) +
                "-" +
                str.substring(6)
            );
        } catch (e) {
            return str;
        }
    }

    // ===============================
    // 8. KEY-VALUE OPERATIONS
    // ===============================

    static getValueByKey(str: string, key: string): string | null {
        const collection: Array<string> = str.split(";");
        for (let i = 0; i < collection.length; i++) {
            if (StringUtils.contains(collection[i], ":")) {
                const pairs = collection[i].split(":");
                if (pairs[0] === key) {
                    return pairs[1];
                }
            }
        }
        return null;
    }

    static setValueByKey(str: string, key: string, replacement: string): string {
        const collection: Array<string> = str.split(";");
        const returnCollection: Array<string> = [];
        for (let i = 0; i < collection.length; i++) {
            if (StringUtils.contains(collection[i], ":")) {
                const pairs = collection[i].split(":");
                if (pairs[0] === key) {
                    pairs[1] = replacement;
                }
                returnCollection.push(pairs.join(":"));
            }
        }
        return returnCollection.join(";");
    }
} 